import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Download, ClipboardList, Wrench, Calendar, Tag, ArrowRight, CheckCircle2 } from "lucide-react";
import { useCopy, useLocale } from "@/i18n/LocaleContext";
import { stripLocale } from "@/i18n/locale";
import { localizedAssetUrl } from "@/i18n/assets";

const COPY = {
  en: {
    languageLabels: ["English", "Spanish", "French", "Portuguese", "Chinese (Mandarin)", "Vietnamese", "Arabic"],
    dismiss: "Dismiss",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    languageLabel: "Preferred language",
    otherLanguageOption: "Other language",
    otherLanguagePlaceholder: "Type the language",
    errorMessage: "Something went wrong. Check your email and try again.",
    sending: "Sending…",
    spamNotice: "One email, no spam. Unsubscribe any time.",
    submittedEyebrow: "You're in",
    submittedTitle: "Check your inbox",
    submittedBlurb: (email: string) => `We'll email a copy to ${email}. You can also grab it now:`,
    downloadBtn: "Open the resource",
    closeBtn: "Close",
    magnets: {
      "products-spec-sheet": { eyebrow: "Free spec guide", title: "Trailer Axle & Bearing Cheat Sheet", blurb: "One-page reference: Dexter spindle codes, common bearing pairs, hub face widths, and bolt patterns. Print it, keep it in the shop.", cta: "Send me the cheat sheet" },
      "blog-seasonal-calendar": { eyebrow: "Free download", title: "Seasonal Trailer Maintenance Calendar", blurb: "A month-by-month maintenance plan for light-duty trailers - the same one our shop techs use to keep customers off the side of the road.", cta: "Send me the calendar" },
      "locations-25-off": { eyebrow: "Local offer", title: "$25 off your first service", blurb: "New customers get $25 off their first repair or inspection at our Elkhart shop. Print the voucher or show it on your phone at the counter.", cta: "Email me the voucher" },
      "default-pretow-checklist": { eyebrow: "Free checklist", title: "12-Point Pre-Tow Inspection", blurb: "The exact walkaround our techs run before every road test - bearings, lights, brakes, tires, coupler. Print it and stash it in the tow rig.", cta: "Send me the checklist" }
    }
  },
  es: {
    languageLabels: ["Inglés", "Español", "Francés", "Portugués", "Chino (mandarín)", "Vietnamita", "Árabe"],
    emailLabel: "Correo electrónico",
    dismiss: "Cerrar",
    emailPlaceholder: "correo@ejemplo.com",
    languageLabel: "Idioma preferido",
    otherLanguageOption: "Otro idioma",
    otherLanguagePlaceholder: "Escriba el idioma",
    errorMessage: "Algo salió mal. Verifique su correo electrónico e intente de nuevo.",
    sending: "Enviando…",
    spamNotice: "Un solo correo, sin spam. Puede darse de baja en cualquier momento.",
    submittedEyebrow: "Ya está registrado",
    submittedTitle: "Revise su bandeja de entrada",
    submittedBlurb: (email: string) => `Enviaremos una copia a ${email}. También puede obtenerla ahora:`,
    downloadBtn: "Abrir el recurso (en inglés)",
    closeBtn: "Cerrar",
    magnets: {
      "products-spec-sheet": { eyebrow: "Guía técnica gratuita", title: "Hoja de referencia de ejes y rodamientos para remolques", blurb: "Referencia de una página: códigos de husillo Dexter, pares comunes de rodamientos, anchos de maza y patrones de pernos. Imprímala y téngala en el taller.", cta: "Enviar la hoja de referencia" },
      "blog-seasonal-calendar": { eyebrow: "Descarga gratuita", title: "Calendario de mantenimiento estacional de remolques", blurb: "Un plan de mantenimiento mes a mes para remolques ligeros: el mismo que usan nuestros técnicos para evitar que los clientes queden varados en la carretera.", cta: "Enviar el calendario" },
      "locations-25-off": { eyebrow: "Oferta local", title: "$25 de descuento en su primer servicio", blurb: "Los nuevos clientes reciben $25 de descuento en su primera reparación o inspección en nuestro taller de Elkhart. Imprima el cupón o muéstrelo en su teléfono en el mostrador.", cta: "Enviar el cupón por correo" },
      "default-pretow-checklist": { eyebrow: "Lista de verificación gratuita", title: "Inspección de 12 puntos antes de remolcar", blurb: "La inspección que realizan nuestros técnicos antes de cada prueba de manejo: rodamientos, luces, frenos, llantas, enganche. Imprímala y guárdela en su vehículo.", cta: "Enviar la lista de verificación" }
    }
  }
};

type MagnetConfig = { id: string; match: (pathname: string) => boolean; Icon: typeof ClipboardList; resource: string; };

const MAGNETS: MagnetConfig[] = [
  { id: "products-spec-sheet", match: (p) => p.startsWith("/products"), Icon: Wrench, resource: "/lead-magnets/axle-bearing-cheat-sheet.html" },
  { id: "blog-seasonal-calendar", match: (p) => p.startsWith("/blog"), Icon: Calendar, resource: "/lead-magnets/seasonal-maintenance-calendar.html" },
  { id: "locations-25-off", match: (p) => p.startsWith("/locations"), Icon: Tag, resource: "/lead-magnets/first-service-voucher.html" },
  { id: "default-pretow-checklist", match: () => true, Icon: ClipboardList, resource: "/lead-magnets/pre-tow-inspection-checklist.html" },
];

const DELAY_MS = 9000;
const LEAD_LANGUAGES = ["English", "Spanish", "French", "Portuguese", "Chinese (Mandarin)", "Vietnamese", "Arabic"];
const SESSION_KEY = "axle.leadmagnet.session.v1";
const SUBMITTED_KEY = "axle.leadmagnet.submitted.v1";

function loadJson<T>(key: string, fallback: T): T {
  try { const raw = typeof window !== "undefined" ? window.localStorage.getItem(key) : null; return raw ? (JSON.parse(raw) as T) : fallback; } catch { return fallback; }
}

function saveJson(key: string, value: unknown) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

export function LeadMagnet() {
  const t = useCopy(COPY);
  const { pathname } = useLocation();
  const { locale } = useLocale();
  // Spanish resource pages live under /es/lead-magnets/ (generated by the build).
  const resourceHref = (r: string) => localizedAssetUrl(r, locale);
  const magnet = useMemo(() => MAGNETS.find((m) => m.match(stripLocale(pathname))) ?? MAGNETS[MAGNETS.length - 1], [pathname]);
  const content = t.magnets[magnet.id as keyof typeof t.magnets] || t.magnets["default-pretow-checklist"];

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState("English");
  const [submitted, setSubmitted] = useState(false);
  const [errored, setErrored] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setOpen(false); setSubmitted(false); setEmail(""); setLanguage("English"); setErrored(false);
    const dismissed = loadJson<Record<string, boolean>>(SESSION_KEY, {});
    if (dismissed[magnet.id]) return;
    const submittedAll = loadJson<Record<string, boolean>>(SUBMITTED_KEY, {});
    if (submittedAll[magnet.id]) return;
    const t = window.setTimeout(() => setOpen(true), DELAY_MS);
    return () => window.clearTimeout(t);
  }, [magnet.id]);

  function dismiss() { setOpen(false); const dismissed = loadJson<Record<string, boolean>>(SESSION_KEY, {}); dismissed[magnet.id] = true; saveJson(SESSION_KEY, dismissed); }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email)) { setErrored(true); return; }
    setErrored(false); setSending(true);
    try {
      const res = await fetch("/api/leads", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, magnetId: magnet.id, pathname: stripLocale(pathname), preferredLanguage: language }), });
      if (!res.ok) { setErrored(true); setSending(false); return; }
      const submittedAll = loadJson<Record<string, boolean>>(SUBMITTED_KEY, {});
      submittedAll[magnet.id] = true; saveJson(SUBMITTED_KEY, submittedAll);
      saveJson(`axle.leadmagnet.email.${magnet.id}`, { email, pathname: stripLocale(pathname), preferredLanguage: language, ts: Date.now() });
      setSubmitted(true);
    } catch { setErrored(true); } finally { setSending(false); }
  }

  if (!open) return null;
  const { Icon } = magnet;

  return (
    <div role="dialog" aria-label={content.title} className="fixed bottom-4 right-4 z-40 w-[calc(100vw-2rem)] max-w-sm rounded-lg border border-border bg-background shadow-2xl shadow-black/30 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <button type="button" onClick={dismiss} aria-label={t.dismiss} className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:bg-secondary/10 hover:text-foreground transition-colors">
        <X className="h-4 w-4" />
      </button>
      {!submitted ? (
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-md bg-primary/10 text-primary p-2.5"><Icon className="h-5 w-5" /></div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-primary-strong font-semibold">{content.eyebrow}</p>
              <h2 className="mt-1 text-base font-semibold leading-snug pr-5">{content.title}</h2>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{content.blurb}</p>
          <form onSubmit={submit} className="mt-4 space-y-2">
            <label htmlFor={`lm-email-${magnet.id}`} className="sr-only">{t.emailLabel}</label>
            <input id={`lm-email-${magnet.id}`} type="email" required autoComplete="email" value={email} onChange={(e) => { setEmail(e.target.value); if (errored) setErrored(false); }} placeholder={t.emailPlaceholder} className={`w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 ${errored ? "border-primary" : "border-border"}`} />
            <label htmlFor={`lm-language-${magnet.id}`} className="block text-xs text-muted-foreground">{t.languageLabel}</label>
            <select id={`lm-language-${magnet.id}`} value={LEAD_LANGUAGES.includes(language) ? language : "__other__"} onChange={(e) => setLanguage(e.target.value === "__other__" ? "" : e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40">
              {LEAD_LANGUAGES.map((l, i) => <option key={l} value={l}>{t.languageLabels[i]}</option>)}
              <option value="__other__">{t.otherLanguageOption}</option>
            </select>
            {!LEAD_LANGUAGES.includes(language) && (<input aria-label={t.otherLanguageOption} required maxLength={80} value={language} onChange={(e) => setLanguage(e.target.value)} placeholder={t.otherLanguagePlaceholder} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />)}
            {errored && <p className="text-xs text-primary-strong">{t.errorMessage}</p>}
            <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium px-3 py-2 hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {sending ? t.sending : <>{content.cta} <ArrowRight className="h-3.5 w-3.5" /></>}
            </button>
          </form>
          <p className="mt-2 text-[11px] text-muted-foreground leading-snug">{t.spamNotice}</p>
        </div>
      ) : (
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-md bg-green-500/10 text-green-600 p-2.5"><CheckCircle2 className="h-5 w-5" /></div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-green-700 font-semibold">{t.submittedEyebrow}</p>
              <h2 className="mt-1 text-base font-semibold leading-snug pr-5">{t.submittedTitle}</h2>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{t.submittedBlurb(email)}</p>
          <a href={resourceHref(magnet.resource)} target="_blank" rel="noopener" className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium px-3 py-2 hover:bg-primary/90 transition-colors">
            <Download className="h-3.5 w-3.5" /> {t.downloadBtn}
          </a>
          <button type="button" onClick={dismiss} className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1">{t.closeBtn}</button>
        </div>
      )}
    </div>
  );
}
